import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DocumentSourceLinkIcon } from 'src/components/Reusable/Sources/sources.styles'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { ModalForm } from 'src/components/ModalForms/ModalForm'
import { MultiSelectForm } from 'src/components/Reusable/Forms/MultiSelectForm'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { GridColumn } from 'src/components/Reusable/Grid/Grid'
import { getPluralization } from 'src/Utils/globalHelpers'
import { NumberSelectedTextPlacement } from 'src/components/ModalForms/ModalForm.styles'
import { ChatContextSelection } from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { StyledInputError } from 'src/components/Reusable/Inputs/TextInput/styles'
import { usePaginateItems } from 'src/components/Reusable/paginate/paginate.hooks'
import { Spinner } from 'src/Throbber'
import { PaginateButtonsGroup } from 'src/components/Reusable/paginate/PaginateButtons'
import {
  ChatContextBooleanSelectorText,
  ChatContextBooleanSelectorPrompt,
  ChatContextBooleanSelectorContainer,
  ChatContextBooleanCheckboxContainer,
  ContextTypeSelectorTitle,
  ContextUpdateStatusContainer,
  ContextTypeSelectorSummary,
  ContextTypeSelectorSummaryContainer,
  FiltersContainer,
  PaginateButtonsContainer,
  ContextIdSelectorRow,
  LinkItem,
} from './chatContextPanel.styles'
import { ContextTypeSelectorContainer } from './chatContextPanel.styles'

const PAGE_SIZE = 100

export type ContextIdItem = {
  id: string
  renderItems: React.ReactNode[]
}

export type ContextIdSelectorProps = {
  title: string
  description: string
  itemType: string
  currentSelection: ChatContextSelection
  onBack?: () => void
  onCommit: (newSelection: ChatContextSelection) => void
  items: ContextIdItem[]
  numberOfColumns: number
  gridTemplateColumnStyling?: string
  show: boolean
  hide: () => void
  isLoading: boolean
  error?: Error | string | null
  filters?: React.ReactNode
  isFilteredResults?: boolean
  modalSize?: 'lg' | 'xl'
  headers?: React.ReactNode
  customActionButton?: React.ReactElement<HTMLButtonElement>
}

export const ContextIdSelector = ({
  title,
  description,
  itemType,
  currentSelection,
  onBack: originalOnBack,
  onCommit,
  items,
  numberOfColumns,
  gridTemplateColumnStyling,
  show,
  hide: originalHide,
  isLoading,
  error,
  filters,
  isFilteredResults,
  modalSize = 'lg',
  headers,
  customActionButton,
}: ContextIdSelectorProps): JSX.Element => {
  const [newSelection, setNewSelection] =
    useState<ChatContextSelection>(currentSelection)

  const { paginatedItems, currentPage, nextPage, prevPage, goToPage } =
    usePaginateItems<ContextIdItem>({
      items,
      pageSize: PAGE_SIZE,
      initialPage: 1,
    })

  useEffect(() => {
    setNewSelection(currentSelection)
    goToPage(1)
  }, [currentSelection, goToPage])

  useEffect(() => {
    goToPage(1)
  }, [items, goToPage])

  const allIds = useMemo(() => items.map((item) => item.id), [items])

  const handleSelectAll = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setNewSelection({
        includeAll: isFilteredResults ? false : true,
        included: isFilteredResults ? new Set(allIds) : new Set(),
      })
    },
    [isFilteredResults, allIds],
  )

  const handleDeselectAll = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setNewSelection({
      includeAll: false,
      included: new Set(),
    })
  }, [])

  const reset = useCallback(() => {
    setNewSelection(currentSelection)
  }, [currentSelection])

  const hide = useCallback(() => {
    reset()
    originalHide()
  }, [reset, originalHide])

  const onBack = useCallback(() => {
    reset()
    originalOnBack?.()
  }, [reset, originalOnBack])

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onCommit(newSelection)
      if (originalOnBack) {
        onBack()
      } else {
        hide()
      }
    },
    [newSelection, onCommit, originalOnBack, onBack, hide],
  )

  const setSelectedItemIds = useCallback(
    (ids: string[]) => {
      const isIncludeAll = ids.length === items.length
      setNewSelection((prev) => ({
        ...prev,
        includeAll: isIncludeAll,
        included: isIncludeAll ? new Set() : new Set(ids),
      }))
    },
    [items.length],
  )

  const selectedItemIds = useMemo(() => {
    if (newSelection.includeAll) {
      return allIds
    }
    return Array.from(newSelection.included || [])
  }, [newSelection, allIds])

  const multiSelectFormProps = useMemo(
    () => ({
      formId: ModalFormId.CHAT_CONTEXT_ID_SELECTOR,
      title: description,
      loading: false,
      gridTemplateColumnStyling: gridTemplateColumnStyling
        ? `min-content ${gridTemplateColumnStyling}`
        : `min-content min-content${' auto'.repeat(numberOfColumns - 1)}`,
      gridItems: items.length ? (
        <>
          {paginatedItems.map((item) => (
            <SelectContextIdRowItem
              key={item.id}
              item={item}
              setSelectedItemIds={setSelectedItemIds}
              selectedItemIds={selectedItemIds}
            />
          ))}
        </>
      ) : (
        <div />
      ),
      numberSelectedText: `${
        selectedItemIds.length === allIds.length ? 'All ' : ''
      }${selectedItemIds.length} ${getPluralization(
        itemType,
        selectedItemIds.length,
      )} selected`,
      numberSelectedTextPlacement: 'top' as NumberSelectedTextPlacement,
      onSubmit,
    }),
    [
      items,
      selectedItemIds,
      description,
      onSubmit,
      itemType,
      setSelectedItemIds,
      allIds.length,
      paginatedItems,
      numberOfColumns,
      gridTemplateColumnStyling,
    ],
  )

  const filtersWithPagination = useMemo(
    () => (
      <FiltersContainer onClick={(e) => void e.preventDefault()}>
        <div style={{ width: '100%' }}>{filters}</div>
        {items.length > 0 && (
          <PaginateButtonsContainer>
            <PaginateButtonsGroup
              totalItems={items.length}
              pageSize={PAGE_SIZE}
              useCustomPagesize={false}
              currentPage={currentPage}
              onPrevClick={prevPage}
              onNextClick={nextPage}
            />
          </PaginateButtonsContainer>
        )}
      </FiltersContainer>
    ),
    [filters, currentPage, prevPage, nextPage, items.length],
  )

  const footer = useMemo(
    () => (
      <ContextTypeSelectorContainer $fullHeight={true}>
        {error ? (
          <StatusPanel
            busy={false}
            error={
              typeof error === 'object' && 'message' in error
                ? error.message
                : error
            }
          />
        ) : (
          <ContextTypeSelectorTitle $center>
            {isLoading ? (
              <Spinner size="m" color="primary" />
            ) : !items.length ? (
              'No data found'
            ) : null}
          </ContextTypeSelectorTitle>
        )}
      </ContextTypeSelectorContainer>
    ),
    [error, isLoading, items.length],
  )

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={ModalFormId.CHAT_CONTEXT_ID_SELECTOR}
      size={modalSize}
      title={title}
      submitText={originalOnBack ? 'Continue' : 'Confirm'}
      showOverflow
      disableSubmitButton={!items.length}
      hideBack={!originalOnBack}
      onBack={onBack}
      customActionButton={customActionButton}
    >
      <form id={ModalFormId.CHAT_CONTEXT_ID_SELECTOR} onSubmit={onSubmit}>
        <MultiSelectForm
          {...multiSelectFormProps}
          showSelectButtons
          handleDeselectAll={handleDeselectAll}
          handleSelectAll={handleSelectAll}
          gridRowGap={5}
          gridAutoFlow="row"
          filters={filtersWithPagination}
          headers={headers}
          footer={footer}
          fullHeight={true}
        />
      </form>
    </ModalForm>
  )
}

type SelectContextIdRowItemProps = {
  item: ContextIdItem
  setSelectedItemIds: (ids: string[]) => void
  selectedItemIds: string[]
}
const SelectContextIdRowItem = ({
  item,
  setSelectedItemIds,
  selectedItemIds,
}: SelectContextIdRowItemProps) => {
  const data = useMemo(() => new Set(selectedItemIds), [selectedItemIds])

  const handleChange = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>,
    ) => {
      e.stopPropagation()
      const newData = new Set(data)
      if (data.has(item.id)) {
        newData.delete(item.id)
      } else {
        newData.add(item.id)
      }
      setSelectedItemIds(Array.from(newData))
    },
    [item.id, setSelectedItemIds, data],
  )

  return (
    <ContextIdSelectorRow
      id={`select-context-id-item-${item.id}`}
      onClick={handleChange}
    >
      <GridColumn>
        <CheckBoxInput id={item.id} checked={data.has(item.id)} />
      </GridColumn>
      {item.renderItems.map((item, index) => (
        <GridColumn minHeight={59} key={index}>
          {item}
        </GridColumn>
      ))}
    </ContextIdSelectorRow>
  )
}

export type ChatContextBooleanSelectorProps = {
  title: string
  description: React.ReactNode
  prompt: string
  currentSelection: boolean
  onCommit: (newSelection: boolean) => void
  show: boolean
  hide: () => void
  onBack?: () => void
}

export const ChatContextBooleanSelector = ({
  title,
  description,
  prompt,
  currentSelection,
  onCommit,
  show,
  hide: originalHide,
  onBack: originalOnBack,
}: ChatContextBooleanSelectorProps): JSX.Element => {
  const [includeAll, setIncludeAll] = useState(currentSelection)

  useEffect(() => {
    setIncludeAll(currentSelection)
  }, [currentSelection])

  const reset = useCallback(() => {
    setIncludeAll(currentSelection)
  }, [currentSelection])

  const hide = useCallback(() => {
    reset()
    originalHide()
  }, [reset, originalHide])

  const onBack = useCallback(() => {
    reset()
    originalOnBack?.()
  }, [reset, originalOnBack])

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onCommit(includeAll)
      if (originalOnBack) {
        onBack()
      } else {
        hide()
      }
    },
    [includeAll, onCommit, originalOnBack, onBack, hide],
  )

  const handleChange = useCallback(() => {
    setIncludeAll((prev) => !prev)
  }, [])

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={ModalFormId.CHAT_CONTEXT_BOOLEAN_SELECTOR}
      size="lg"
      title={title}
      submitText={originalOnBack ? 'Continue' : 'Confirm'}
      showOverflow
      hideBack={!originalOnBack}
      onBack={onBack}
    >
      <form id={ModalFormId.CHAT_CONTEXT_BOOLEAN_SELECTOR} onSubmit={onSubmit}>
        <ChatContextBooleanSelectorContainer>
          <ChatContextBooleanSelectorText>
            {description}
          </ChatContextBooleanSelectorText>
          <ChatContextBooleanCheckboxContainer>
            <CheckBoxInput
              id={'boolean-selector-checkbox'}
              checked={includeAll}
              onClick={(e) => e.stopPropagation()}
              onChange={handleChange}
            />
            <ChatContextBooleanSelectorPrompt>
              {prompt}
            </ChatContextBooleanSelectorPrompt>
          </ChatContextBooleanCheckboxContainer>
        </ChatContextBooleanSelectorContainer>
      </form>
    </ModalForm>
  )
}

export type StatusPanelProps = {
  busy: boolean
  error: string | undefined | null
}
export const StatusPanel = ({ busy, error }: StatusPanelProps): JSX.Element => {
  return (
    <ContextUpdateStatusContainer>
      {busy && <Spinner size="m" color="primary" />}
      {error && (
        <StyledInputError showError={!!error}>{error}</StyledInputError>
      )}
    </ContextUpdateStatusContainer>
  )
}

export type SummaryProps = {
  summary: string | string[]
  centerAligned?: boolean
}

export const Summary = ({
  summary,
  centerAligned,
}: SummaryProps): JSX.Element => {
  const summaryLines = useMemo(
    () => (Array.isArray(summary) ? summary : [summary]),
    [summary],
  )

  return (
    <ContextTypeSelectorSummaryContainer>
      {summaryLines.map((line, index) => (
        <ContextTypeSelectorSummary key={index} $centerAligned={centerAligned}>
          {line}
        </ContextTypeSelectorSummary>
      ))}
    </ContextTypeSelectorSummaryContainer>
  )
}

export type GridRowLinkItemProps = {
  link: string
  text: string
}

export const GridRowLinkItem = ({
  link,
  text,
}: GridRowLinkItemProps): JSX.Element => {
  const onClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
  }, [])

  return (
    <LinkItem to={link} target="_blank" rel="noreferrer" onClick={onClick}>
      {text} <DocumentSourceLinkIcon />
    </LinkItem>
  )
}
